import { TypographyV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import Motion from '@/app/components/Motion';

interface Orientation {
  x: 'left' | 'right';
  y: 'top' | 'bottom';
}

export interface PopHighlightsRingTooltipProps {
  // The text content of the tooltip
  readonly content: MessageKey;
  // Which quadrant the tooltip should appear in
  readonly orientation: Orientation;
  // Positioning for the center of the tooltip's bounding box (in percentages)
  // relative to the top left corner of ring graphic
  readonly position: {
    x: number;
    y: number;
  };
}

function Label({
  content,
  orientation,
}: Omit<PopHighlightsRingTooltipProps, 'position'>): JSX.Element {
  const t = useTranslations();
  const isTop = orientation.y === 'top';
  const isBottom = orientation.y === 'bottom';
  const isLeft = orientation.x === 'left';
  const isRight = orientation.x === 'right';

  return (
    <AnimatePresence>
      <Motion
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        key="label"
        transition={{ delay: 0.2, duration: 0.1 }}
      >
        <div
          className={cx(
            'absolute z-10 whitespace-nowrap rounded-lg bg-gray-600 px-3 py-2 text-sandstone-200',
            {
              'top-[1px]': isTop,
              'bottom-[1px]': isBottom,
              'left-1/2': isRight,
              'right-1/2': isLeft,
              '-translate-y-full': isTop,
              'translate-y-full': isBottom,
            },
          )}
          data-cy="pop_highlights_ring_tooltip_label"
        >
          <TypographyV3
            className="block uppercase leading-none tracking-wider"
            color="inherit"
            Element="span"
            variant="eyebrow-small"
            weight="bold"
          >
            {t(content)}
          </TypographyV3>
        </div>
      </Motion>
    </AnimatePresence>
  );
}

function Line({
  orientation,
}: {
  readonly orientation: Orientation;
}): JSX.Element {
  const isTop = orientation.y === 'top';
  const isBottom = orientation.y === 'bottom';
  const isLeft = orientation.x === 'left';
  const isRight = orientation.x === 'right';

  return (
    <AnimatePresence>
      <Motion
        animate={{ scale: 1 }}
        className="relative size-full"
        initial={{ scale: 0 }}
        key="line"
        style={{ originX: 1, originY: 1 }}
        transition={{ delay: 0.1, duration: 0.1 }}
      >
        <div
          className={cx('absolute h-[3px] w-[140%] bg-gray-600', {
            'bottom-0': isTop,
            'top-0': isBottom,
            'left-0': isRight,
            'right-0': isLeft,
            'origin-bottom-right': isLeft && isTop,
            'origin-bottom-left': isRight && isTop,
            'origin-top-right': isLeft && isBottom,
            'origin-top-left': isRight && isBottom,
            'rotate-45': (isTop && isLeft) || (isBottom && isRight),
            '-rotate-45': (isBottom && isLeft) || (isTop && isRight),
          })}
          data-cy="pop_highlights_ring_tooltip_line"
        />
      </Motion>
    </AnimatePresence>
  );
}

function Dot({
  orientation,
}: {
  readonly orientation: Orientation;
}): JSX.Element {
  const isTop = orientation.y === 'top';
  const isBottom = orientation.y === 'bottom';
  const isLeft = orientation.x === 'left';
  const isRight = orientation.x === 'right';

  return (
    <AnimatePresence>
      <Motion
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        key="dot"
        transition={{ duration: 0.1 }}
      >
        <div
          className={cx('absolute size-5 rounded-full bg-gray-600', {
            'bottom-0': isTop,
            'top-0': isBottom,
            'left-0': isRight,
            'right-0': isLeft,
            '-translate-x-1/2': isRight,
            'translate-x-1/2': isLeft,
            'translate-y-1/2': isTop,
            '-translate-y-1/2': isBottom,
          })}
          data-cy="pop_highlights_ring_tooltip_dot"
        />
      </Motion>
    </AnimatePresence>
  );
}

export function PopHighlightsRingTooltip({
  content,
  orientation,
  position,
}: PopHighlightsRingTooltipProps): JSX.Element {
  return (
    <div
      aria-hidden="true"
      className="absolute z-10 hidden size-16 -translate-x-1/2 -translate-y-1/2 xl:block"
      data-cy="pop_highlights_ring_tooltip"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
      }}
    >
      <Label content={content} orientation={orientation} />
      <Line orientation={orientation} />
      <Dot orientation={orientation} />
    </div>
  );
}
